import React, { useEffect, useContext, useState, useCallback } from 'react';
import { NavigationContext } from '../../../context/navigation-context';
import { Button, Spinner, Tooltip, OverlayTrigger } from "react-bootstrap";
import useFetch from '../../../hooks/fetchMSSQL-hook';
import ReportForm from '../../../components/Modals/ReportForm';
import { getReportSheet } from "../../../util/getReportSheet";
import { API_URL } from "../../../config/config";

const Reports = () => {
  const { setHeaderData } = useContext(NavigationContext);
  const { fetchMssqlApi } = useFetch();
  const [reports, setReports] = useState([]);
  const [reportConditions, setReportConditions] = useState([]);
  const [timeSpans, setTimeSpans] = useState([]);
  const [reportTasks, setReportTasks] = useState([]);
  const [filter, setFilter] = useState({reportName: "", name: "", time: "", createTime: "", shared: null, sortBy: "none"});
  const [openModal, setOpenModal] = useState({open: false, reportConditionId: undefined, reportId: undefined, reportConditionName: undefined});
  const [downloading, setDownloading] = useState([]);
  const [loadingEdit, setLoadingEdit] = useState(null);
  const [loadingScheduler, setLoadingScheduler] = useState(null);
  const reportConditionsMerged = React.useMemo(() => {
    if (!reports.length || !reportConditions.length || !timeSpans.length) return [];
    return reportConditions.map(rc => {
      const report = reports.find(r => r.reportId === rc.ReportId);
      const ts = timeSpans.find(t => t.TimeSpanId === rc.TimeSpanId);
      const rt = reportTasks.find(rt => rt.ReportConditionId === rc.ReportConditionId)
      return {...rc, 
        ReportName: report?.reportName || "", 
        ReportDescription: report?.reportDescription || "", 
        TimeSpanName: ts?.Name || "", 
        ReportTaskId: rt?.ReportTaskId || "",
        LastEnd: rt?.LastEnd || "", 
        RecurringEvery: rt?.RecurringEvery ?? "",
        UserEmails: rt?.UserEmails || [],
        UserIds: rt?.UserEmails || [],
      };
    });
  }, [reports, reportConditions, timeSpans, reportTasks]);

  const sortDefaults = {
    ReportName: "ASC",
    Name: "ASC",
    TimeSpanName: "ASC",
    CreateDateTime: "DESC",
    RecurringEvery: "ASC",
  };

  const getSortState = () => {
    const [field, dir] = String(filter.sortBy || "").split("|");
    return { field, dir: (dir || "").toUpperCase() };
  };

  const sortState = getSortState();

  const arrow = (field) => {
    if (sortState.field !== field) return "";
    return sortState.dir === "ASC" ? " ▲" : " ▼";
  };

  const handleHeaderSort = (field) => {
    const [curField, curDirRaw] = String(filter.sortBy || "").split("|");
    const curDir = (curDirRaw || "").toUpperCase();

    let nextDir;
    if (curField === field) {
      nextDir = curDir === "ASC" ? "DESC" : "ASC";
    } else {
      nextDir = sortDefaults[field] || "ASC";
    }

    const next = { ...filter, sortBy: `${field}|${nextDir}` };
    setFilter(next);
    getReportConditions(next);
  };

  const getReports = useCallback(() => {
    fetchMssqlApi(`getReports`, { method: 'GET', hideNotification: true }, (response) => {
      const Response = response[0]?.map((r) => ({
        ...r,
        timeSpans: (() => {try {if (Array.isArray(r.timeSpans)) return r.timeSpans; if (typeof r.timeSpans === "string") return JSON.parse(r.timeSpans); return [];} catch { return [];}})()
      }))
      setReports(Response);
    });
  }, [fetchMssqlApi]);

  const getReportConditions = useCallback ((f) => {
    fetchMssqlApi(
      `reports/getReportConditions`,
      {
        method: 'GET',
        hideNotification: true,
        showLoader: true,
        params: {
          sortBy: f?.sortBy
        }
      },
      (response) => {setReportConditions(response[0])}
    )
  }, [fetchMssqlApi])
  
  const getTimeSpans = useCallback (() => {fetchMssqlApi(`reports/getTimeSpans`, { method: 'GET', hideNotification: true}, (response) => {setTimeSpans(response[0])})}, [fetchMssqlApi])

  const getReportTasks = useCallback (() => {fetchMssqlApi('reports/getReportTasks', { method: 'GET', hideNotification: true}, (response) => {setReportTasks(response)})}, [fetchMssqlApi])

  const handleDownload = (reportId, reportConditionId) => {
    setDownloading(prev => ([...prev, reportConditionId]));
    const url = `${API_URL}/api/reports/${reportId}`;
    getReportSheet({url, reportId, body: {reportConditionId}}).finally(() => setDownloading(prev => prev.filter(item => item !== reportConditionId)))
  }

  const parseRecurringEvery = (recurringTime) => {
    if (recurringTime === 0) return "Jednorazowo";
    if (recurringTime === 3600) return "Raz na godzine";
    if (recurringTime === 21600) return "Raz na 6 godzin";
    if (recurringTime === 43200) return "Raz na 12 godzin";
    if (recurringTime === 86400) return "Raz na dobę";
    if (recurringTime === 604800) return "Raz na tydzień";
    if (recurringTime === 2592000) return "Raz na miesiąc";
  }

  useEffect(() => {
    setHeaderData({ text: "Raporty" });
    getReports();
    getReportConditions(filter);
    getTimeSpans();
    getReportTasks();
  }, [setHeaderData]);

  const reportConditionsFiltered = React.useMemo(() => {
    if (!reportConditionsMerged) return [];
    const f = {
      reportName: filter.reportName?.toLowerCase() ?? "",
      name: filter.name?.toLowerCase() ?? "",
      time: filter.time?.toLowerCase() ?? "",
      createTime: filter.createTime?.toLowerCase() ?? "",
      lastTime: filter.lastTime?.toLowerCase() ?? "",
      recurringTime: filter.recurringTime?.toLowerCase() ?? "",
    };
    return reportConditionsMerged.filter(rc => {
      return (
        `${rc.ReportName} DZ${rc.ReportId}`.toLowerCase().includes(f.reportName) &&
        rc.Name?.toLowerCase().includes(f.name) &&
        rc.TimeSpanName?.toLowerCase().includes(f.time) &&
        rc.CreateDateTime?.toLowerCase().includes(f.createTime) &&
        rc.LastEnd?.toLowerCase().includes(f.lastTime) &&
        (!rc.RecurringEvery || parseRecurringEvery(rc.RecurringEvery)?.toLowerCase().includes(f.recurringTime)) &&
        (filter.shared === null || rc.IsShared === filter.shared)
      );
    });
  }, [reportConditionsMerged, filter]);

  return (
    <>
      <div className='border'>
	      <div className='custom-scroll overflow-auto' style={{maxHeight: window.innerHeight-110, overflowY: "auto"}}>
          <table className="table table-striped">
            <thead className="headers-stick-machines">
              <tr className="text-center">
                <th colSpan={2}>
                  <div className="d-flex flex-column align-items-center">
                  <div style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("ReportName")}>
                    Raport{arrow("ReportName")}
                  </div>
                  <input type="text" className="form-control form-control-sm" onChange={({target:{value}}) => setFilter(prev => ({...prev, reportName: value}))} style={{maxHeight: "20px", maxWidth: "150px"}}/>
                  </div>
                </th>
                <th>
                  <div className="d-flex flex-column align-items-center">
                  <div style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("Name")}>
                    Nazwa raportu{arrow("Name")}
                  </div>
                  <input type="text" className="form-control form-control-sm" onChange={({target:{ value }}) => setFilter(prev => ({...prev, name: value}))} style={{maxHeight: "20px", maxWidth: "150px"}}/>
                  </div>  
                </th>
                <th>
                  <div className="d-flex flex-column align-items-center">
                    <div style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("TimeSpanName")}>
                      Zakres czasowy{arrow("TimeSpanName")}
                    </div>
                    <input type="text" className="form-control form-control-sm" onChange={({target:{value}}) => setFilter(prev => ({...prev, time: value}))} style={{maxHeight: "20px", maxWidth: "150px"}}/>
                  </div>
                </th>
                <th>
                  <div className="d-flex flex-column align-items-center">
                    <div style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("CreateDateTime")}>
                      Data stworzenia{arrow("CreateDateTime")}
                    </div>
                    <input type="text" className="form-control form-control-sm" onChange={({target:{value}}) => setFilter(prev => ({...prev, createTime: value}))} style={{maxHeight: "20px", maxWidth: "150px"}}/>
                  </div>
                </th>
                <th>
                  <div className="d-flex flex-column align-items-center">
                    <div style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleHeaderSort("RecurringEvery")}>
                      Częstotliwość{arrow("RecurringEvery")}
                    </div>
                    <input type="text" className="form-control form-control-sm" onChange={({target:{value}}) => setFilter(prev => ({...prev, recurringTime: value}))} style={{maxHeight: "20px", maxWidth: "150px"}}/>
                  </div>
                </th>
                <th>
                  <div className="btn font-weight-bold" style={{fontSize: "12px"}} onClick={() => {setFilter(prev => ({...prev, shared: prev.shared === null ? 1 : prev.shared === 1 ? 0 : null}))}}>
                    Współdzielony
                  </div>
                </th>
                <th className="pl-2 pr-2" style={{width: "25px"}}><i className="fas fa-download"/></th>
                <th className="pl-0 pr-2" style={{width: "25px"}}><i className="fas fa-history"/></th>
                <th className="pl-0 pr-2" style={{width: "25px"}}><i className="fas fa-plus btn btn-outline-secondary" onClick={() => setOpenModal({open: true, reportConditionId: undefined, reportId: undefined})}/></th>
              </tr>
            </thead>
            <tbody>
              {reportConditionsFiltered?.map((rc) => (
                <tr key={rc.ReportConditionId}>
                  <td className="p-1" style={{maxWidth: "50px", minWidth: "50px"}}>
                    <OverlayTrigger placement={"bottom"} overlay={<Tooltip id={`tooltip-${rc.ReportId}`}>{rc.ReportDescription}</Tooltip>}>
                      <span className="text-primary" style={{ cursor: "pointer" }}>DZ{rc.ReportId}</span>
                    </OverlayTrigger>
                  </td>
                  <td className="p-1" style={{minWidth: "200px"}}>{rc.ReportName}</td>
                  <td className="p-1 text-center" style={{minWidth: "250px"}}>{rc.Name}</td>
                  <td className="p-1 text-center" style={{minWidth: "150px"}}>{rc.TimeSpanName}</td>
                  <td className="p-1 text-center" style={{minWidth: "150px"}} title={rc.CreateDateTime}>{rc.CreateDateTime}</td>
                  <td className="p-1 text-center" style={{minWidth: "150px"}}>{parseRecurringEvery(rc.RecurringEvery)}</td>
                  <td className="p-1 text-center text-primary" style={{minWidth: "150px"}}><i className={rc.IsShared ? "fas fa-check-square" : "far fa-square"}/></td>
                  <td className="text-center pl-2 pr-2">
                    {!(downloading.includes(rc.ReportConditionId)) ?
                      (<Button variant="primary" className="btn fas fa-xs fa-download" onClick={() => handleDownload(rc.ReportId, rc.ReportConditionId)}/>)
                    : (<Button variant="primary" className="btn fas fa-xs"><i className="fas fa-xs fa-circle-notch fa-spin" style={{fontSize: "0.75rem"}}/></Button>)}
                  </td>
                  <td className="text-center pl-0 pr-2">
                    {!(loadingScheduler === rc.ReportConditionId) ? 
                      (<Button variant={`${rc?.ReportTaskId ? "dark" : "outline-dark"}`} className={`btn fas fa-xs ${rc?.ReportTaskId ? "fa-history" : "fa-plus"}`} style={{ pointerEvents: (loadingScheduler !== null ? "none" : "auto"), width:"36.5px"}} onClick={() => {setLoadingScheduler(rc.ReportConditionId); setOpenModal({ open: true, reportConditionId: rc.ReportConditionId, reportId: rc.ReportId, reportConditionName: rc.Name})}}/>)
                    : (<Button variant={`${rc?.ReportTaskId ? "dark" : "outline-dark"}`} className="btn fas fa-xs"><i className="fas fa-xs fa-circle-notch fa-spin" style={{fontSize: "0.75rem"}}/></Button>)}
                  </td>
                  <td className="text-center pl-0 pr-2">
                    {!(loadingEdit === rc.ReportConditionId) ? 
                      (<Button variant="secondary" className="btn fas fa-xs fa-edit" style={{ pointerEvents: (loadingEdit !== null ? "none" : "auto")}} onClick={() => {setLoadingEdit(rc.ReportConditionId); setOpenModal({ open: true, reportConditionId: rc.ReportConditionId, reportId: rc.ReportId})}}/>)
                    : (<Button variant="secondary" className="btn fas fa-xs"><i className="fas fa-xs fa-circle-notch fa-spin" style={{fontSize: "0.75rem", fontWeight: "700"}}/></Button>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ReportForm show={(openModal.open && (loadingEdit === null))} onHide={() => setOpenModal({open: false, reportConditionId: undefined, reportId: undefined, reportConditionName: undefined})} onConfirm={() => {getReportConditions(filter); getReportTasks()}} reportList={reports.filter(r => r.canUserEditCondition === 1)} reportConditionId={openModal.reportConditionId} reportConditionName={openModal.reportConditionName} reportId={openModal.reportId} loadingEdit={loadingEdit} setLoadingEdit={setLoadingEdit} loadingScheduler={loadingScheduler} setLoadingScheduler={setLoadingScheduler} reportTask={reportTasks.filter(r => r.ReportConditionId === openModal.reportConditionId)?.[0]}/>
    </>
  );
};

export default Reports;